import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  Settings,
  Save,
  Lock,
  LockOpen,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  ContentCopy,
  Refresh,
  RestartAlt,
  Grass
} from '@mui/icons-material';
import adminSettingsService from '../models/AdminSettingsService';

const GradientCard = styled(Card)(({ theme, variant = 'primary' }) => {
  const gradients = {
    primary: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
    secondary: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
    warning: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
    danger: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)'
  };

  return {
    background: gradients[variant],
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '24px',
    boxShadow: `
      0 20px 40px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1)
    `,
    overflow: 'hidden',
    position: 'relative',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `
        0 32px 64px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.2)
      `,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: variant === 'primary' 
        ? 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
        : variant === 'secondary'
        ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
        : variant === 'warning'
        ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
        : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
    }
  };
});

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.9),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.background.paper, 1),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    }
  }
}));

const AnimatedButton = styled(Button)(({ theme, variant: buttonVariant = 'primary' }) => {
  const colors = {
    primary: {
      bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      hover: 'linear-gradient(135deg, #5a6fd8 0%, #6b4190 100%)'
    },
    secondary: {
      bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      hover: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
    },
    danger: {
      bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      hover: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
    }
  };

  return {
    borderRadius: '12px',
    padding: '12px 24px',
    background: colors[buttonVariant]?.bg || colors.primary.bg,
    color: 'white',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: colors[buttonVariant]?.hover || colors.primary.hover,
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 48px rgba(0, 0, 0, 0.18)',
    },
    '&:active': {
      transform: 'translateY(-1px)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.5s',
    },
    '&:hover::before': {
      left: '100%',
    }
  };
});

function AutomowerConfig() {
  const [settings, setSettings] = useState({
    clientId: '',
    clientSecret: '',
    redirectUri: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const loadedSettings = await adminSettingsService.getAutomowerSettings();
      setSettings({
        clientId: loadedSettings.clientId || '',
        clientSecret: loadedSettings.clientSecret || '',
        redirectUri: loadedSettings.redirectUri || `${window.location.origin}/automower/callback`
      });
      
      if (loadedSettings.source === 'localStorage') {
        setMessage({
          type: 'warning',
          text: 'Paramètres chargés depuis le stockage local (Firebase indisponible)'
        });
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors du chargement des paramètres'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const result = await adminSettingsService.saveAutomowerSettings(settings);
      
      setMessage({
        type: 'success',
        text: result.message
      });
      
      setTimeout(() => setMessage(null), 5000);
      
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Erreur lors de la sauvegarde'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({
      type: 'info',
      text: 'Copié dans le presse-papier'
    });
    setTimeout(() => setMessage(null), 2000);
  };

  const resetToDefaults = () => {
    setSettings({
      clientId: '',
      clientSecret: '',
      redirectUri: `${window.location.origin}/automower/callback`
    });
    setDialogOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Message d'alerte */}
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3, borderRadius: '16px' }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Configuration OAuth */}
        <Grid item xs={12} md={8}>
          <GradientCard variant="primary">
            <CardContent sx={{ p: 4 }}>
              <Box display="flex" alignItems="center" mb={3}>
                <Grass sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h5" fontWeight={600}>
                  Configuration OAuth Husqvarna
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ModernTextField
                    fullWidth
                    label="Client ID"
                    value={settings.clientId}
                    onChange={(e) => handleInputChange('clientId', e.target.value)}
                    placeholder="Votre Client ID Husqvarna"
                    InputProps={{
                      endAdornment: settings.clientId && (
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(settings.clientId)}
                        >
                          <ContentCopy />
                        </IconButton>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <ModernTextField
                    fullWidth
                    label="Client Secret"
                    type={showSecrets ? 'text' : 'password'}
                    value={settings.clientSecret}
                    onChange={(e) => handleInputChange('clientSecret', e.target.value)}
                    placeholder="Votre Client Secret Husqvarna"
                    InputProps={{
                      endAdornment: (
                        <Box display="flex" alignItems="center">
                          <IconButton 
                            size="small"
                            onClick={() => setShowSecrets(!showSecrets)}
                          >
                            {showSecrets ? <Lock /> : <LockOpen />}
                          </IconButton>
                          {settings.clientSecret && (
                            <IconButton 
                              size="small" 
                              onClick={() => copyToClipboard(settings.clientSecret)}
                            >
                              <ContentCopy />
                            </IconButton>
                          )}
                        </Box>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <ModernTextField
                    fullWidth
                    label="Redirect URI"
                    value={settings.redirectUri}
                    onChange={(e) => handleInputChange('redirectUri', e.target.value)}
                    placeholder="URL de redirection OAuth"
                    InputProps={{
                      endAdornment: settings.redirectUri && (
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(settings.redirectUri)}
                        >
                          <ContentCopy />
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
              </Grid>

              <Box mt={4} display="flex" gap={2} flexWrap="wrap">
                <AnimatedButton
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSave}
                  disabled={saving || !settings.clientId || !settings.clientSecret}
                  buttonVariant="primary"
                >
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </AnimatedButton>

                <AnimatedButton
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadSettings}
                  buttonVariant="secondary"
                >
                  Recharger
                </AnimatedButton>

                <AnimatedButton
                  variant="outlined"
                  startIcon={<RestartAlt />}
                  onClick={() => setDialogOpen(true)}
                  buttonVariant="danger"
                >
                  Réinitialiser
                </AnimatedButton>
              </Box>
            </CardContent>
          </GradientCard>
        </Grid>

        {/* Informations et aide */}
        <Grid item xs={12} md={4}>
          <GradientCard variant="secondary">
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Info sx={{ mr: 2, color: 'success.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Informations
                </Typography>
              </Box>

              <Typography variant="body2" paragraph>
                Pour configurer l'intégration Husqvarna Automower, vous devez :
              </Typography>

              <Box component="ol" sx={{ pl: 2, typography: 'body2' }}>
                <li>Créer une application sur le portail développeur Husqvarna</li>
                <li>Copier le Client ID et Client Secret</li>
                <li>Configurer l'URL de redirection</li>
                <li>Ajouter vos robots via la gestion des comptes</li>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary">
                <strong>URL de redirection :</strong><br />
                {settings.redirectUri}
              </Typography>
            </CardContent>
          </GradientCard>

          {/* Statut de la configuration */}
          <GradientCard variant={settings.clientId && settings.clientSecret ? 'secondary' : 'warning'} sx={{ mt: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                {settings.clientId && settings.clientSecret ? (
                  <CheckCircle sx={{ mr: 2, color: 'success.main' }} />
                ) : (
                  <Warning sx={{ mr: 2, color: 'warning.main' }} />
                )}
                <Typography variant="h6" fontWeight={600}>
                  Statut
                </Typography>
              </Box>

              <Chip
                label={settings.clientId && settings.clientSecret ? 'Configuré' : 'Non configuré'}
                color={settings.clientId && settings.clientSecret ? 'success' : 'warning'}
                variant="filled"
                sx={{ borderRadius: '8px' }}
              />
            </CardContent>
          </GradientCard>
        </Grid>
      </Grid>

      {/* Dialog de confirmation pour la réinitialisation */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle>
          <Warning sx={{ mr: 1, color: 'warning.main' }} />
          Confirmer la réinitialisation
        </DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir réinitialiser la configuration ? 
            Tous les paramètres seront perdus.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button onClick={resetToDefaults} color="warning" variant="contained">
            Réinitialiser
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AutomowerConfig;